import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { DepreciationReportsService, DepreciationReport, DepreciationReportsResponse } from '../../services/depreciation-reports.service';

@Component({
  selector: 'app-depreciation-reports',
  templateUrl: './depreciation-reports.component.html',
  styleUrls: ['./depreciation-reports.component.css']
})
export class DepreciationReportsComponent implements OnInit {
  displayedColumns: string[] = ['id', 'assetName', 'category', 'purchaseDate', 'purchaseCost', 'currentDepreciatedValue', 'annualDepreciation', 'remainingValue', 'status', 'actions'];
  dataSource = new MatTableDataSource<DepreciationReport>();
  isLoading = false;
  selectedDate: Date = new Date();
  totalAssets: number = 0;
  totalCurrentValue: number = 0;
  totalDepreciation: number = 0;

  constructor(
    private depreciationReportsService: DepreciationReportsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.loadDepreciationReports();
  }

  loadDepreciationReports(): void {
    this.isLoading = true;
    this.depreciationReportsService.getDepreciationReports().subscribe({
      next: (response: DepreciationReportsResponse) => {
        this.dataSource.data = response.reports;
        this.totalAssets = response.activeAssetsCount;
        this.totalCurrentValue = response.totalBookValue;
        this.calculateTotals();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading depreciation reports:', error);
        this.snackBar.open('Error loading depreciation reports', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  calculateTotals(): void {
    // Calculate totals from the reports data (in case API doesn't provide them)
    if (this.dataSource.data.length > 0) {
      this.totalAssets = this.dataSource.data.length;
      this.totalCurrentValue = this.dataSource.data.reduce((sum, item) => sum + item.currentDepreciatedValue, 0);
      this.totalDepreciation = this.dataSource.data.reduce((sum, item) => sum + item.accumulatedDepreciation, 0);
    }
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  dateChange(event: MatDatepickerInputEvent<Date>): void {
    this.selectedDate = event.value || new Date();
    this.loadDepreciationReports(); // Reload with new date filter
  }

  generateReport(): void {
    this.snackBar.open('Generating depreciation report...', 'Close', { duration: 3000 });
    // TODO: Implement report generation
  }

  exportToExcel(): void {
    this.snackBar.open('Exporting to Excel...', 'Close', { duration: 3000 });
    this.depreciationReportsService.exportToExcel().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `depreciation-reports-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.snackBar.open('Excel export completed', 'Close', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error exporting to Excel:', error);
        this.snackBar.open('Error exporting to Excel', 'Close', { duration: 3000 });
      }
    });
  }

  exportToPDF(): void {
    this.snackBar.open('Exporting to PDF...', 'Close', { duration: 3000 });
    this.depreciationReportsService.exportToPDF().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `depreciation-reports-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.snackBar.open('PDF export completed', 'Close', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error exporting to PDF:', error);
        this.snackBar.open('Error exporting to PDF', 'Close', { duration: 3000 });
      }
    });
  }

  viewAssetDetails(asset: DepreciationReport): void {
    this.snackBar.open(`Viewing details for ${asset.assetName}`, 'Close', { duration: 3000 });
    // TODO: Implement asset details dialog
  }

  generateSampleData(): void {
    this.snackBar.open('Generating sample depreciation data...', 'Close', { duration: 3000 });
    this.depreciationReportsService.generateSampleDepreciationData().subscribe({
      next: (response: DepreciationReportsResponse) => {
        this.dataSource.data = response.reports;
        this.totalAssets = response.activeAssetsCount;
        this.totalCurrentValue = response.totalBookValue;
        this.calculateTotals();
        this.snackBar.open('Sample data generated successfully', 'Close', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error generating sample data:', error);
        this.snackBar.open('Error generating sample data', 'Close', { duration: 3000 });
      }
    });
  }
}
